import { z } from 'zod';

/**
 * What `apps/api` answers on `/activation-tracking`, parsed rather than cast.
 *
 * Mirrors the internal contract of NOXSCRUM-1820 field for field. The feature
 * names are NOT known here: every museum's catalog comes from the hub, in its
 * order, so nothing on these screens may hardcode "Email" or "Payouts".
 */

export const MUSEUM_STATUSES = ['not_started', 'in_progress', 'completed'] as const;
export const MuseumStatus = z.enum(MUSEUM_STATUSES);
export type MuseumStatus = z.infer<typeof MuseumStatus>;

/** How the museum first reached Feature activation: the invitation link, or its own tab. */
export const FirstSource = z.enum(['invite', 'tab']);
export type FirstSource = z.infer<typeof FirstSource>;

const FeatureRef = z.object({ id: z.string(), title: z.string() });
export type FeatureRef = z.infer<typeof FeatureRef>;

/**
 * One row of the list.
 *
 * `opened: false` is the museum that never visited the page, and everything
 * after it is empty by construction — `total` included (it arrives as `0`),
 * which is why the list draws no progress for it rather than "0 of 0".
 */
export const ActivationMuseum = z.object({
    tenantId: z.string(),
    name: z.string().nullable(),
    /**
     * The catalogue's own flag. `null` is "it does not say", which is not a
     * claim that this is a scratch org — so the sandbox filter keeps those rows
     * rather than hiding a real museum on a missing field.
     */
    isSandbox: z.boolean().nullable(),
    status: MuseumStatus,
    opened: z.boolean(),
    firstSource: FirstSource.nullable(),
    firstOpenedAt: z.string().nullable(),
    lastOpenedAt: z.string().nullable(),
    done: z.number(),
    total: z.number(),
    /** Checked and found not done. */
    missing: z.array(FeatureRef),
    /** In the catalog, but the hub holds no status for it yet. */
    notSeen: z.array(FeatureRef),
    /** Checked with no answer — neither done nor missing. */
    couldntCheck: z.array(FeatureRef),
});
export type ActivationMuseum = z.infer<typeof ActivationMuseum>;

export const ActivationListResponse = z.object({ museums: z.array(ActivationMuseum) });

/** `email: null` is a user id identity-api does not know — not a failure to ask. */
const Opener = z.object({ userId: z.string(), email: z.string().nullable() });
export type Opener = z.infer<typeof Opener>;

export const FEATURE_STATES = ['done', 'not_done', 'unknown', 'no_data'] as const;
export const FeatureState = z.enum(FEATURE_STATES);
export type FeatureState = z.infer<typeof FeatureState>;

export const ActivationFeature = z.object({
    id: z.string(),
    title: z.string(),
    state: FeatureState,
    checkedAt: z.string().optional(),
    startedAt: z.string().optional(),
    doneSeenAt: z.string().optional(),
});
export type ActivationFeature = z.infer<typeof ActivationFeature>;

/**
 * One museum.
 *
 * `peopleLookup: 'failed'` means identity-api did not answer, so a missing email
 * is "we could not ask", which the screen must say rather than render as blank.
 */
export const ActivationDetail = z.object({
    tenantId: z.string(),
    name: z.string().nullable(),
    status: MuseumStatus,
    opened: z.boolean(),
    done: z.number(),
    total: z.number(),
    activation: z
        .object({
            firstOpenedAt: z.string(),
            lastOpenedAt: z.string(),
            firstSource: FirstSource,
            firstOpenedBy: Opener.nullable(),
            lastOpenedBy: Opener.nullable(),
        })
        .nullable(),
    peopleLookup: z.enum(['ok', 'failed', 'skipped']),
    features: z.array(ActivationFeature),
});
export type ActivationDetail = z.infer<typeof ActivationDetail>;
